import { X, Hash, Folder, Tag, Settings, CircleDashed, Zap, Clock, Sparkles, Archive, ShieldAlert, Flag, GripVertical, Plus, Trash2, Check, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import clsx from 'clsx';
import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { CustomFieldsManager } from '@/components/fields/CustomFieldsManager';
import { TagsManager } from '@/components/fields/TagsManager';
import { useModal } from '@/hooks/useModal';
import { clampToViewport } from '@/hooks/useDropdownPosition';
import { useOrganization } from '@/context/OrganizationContext';
import { generateA11yId, trapFocus } from '@/utils/accessibility';
import { renameSpace } from '@/db/mutations';
import { handleError } from '@/utils/errorHandler';
import { fetchCustomFields, type CustomField } from '@/db/customFields';
import { fetchTaskStatuses, saveTaskStatuses, deleteTaskStatusesByScope } from '@/db/taskStatuses';
import { fetchStatusPresets, createStatusPreset, deleteStatusPreset, type StatusPreset } from '@/db/statusPresets';
import { toast } from '@/utils/toast';
import { useAuth } from '@/context/AuthContext';

// ─── Status icons map ─────────────────────────────────────────────────────────

const STATUS_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  CircleDashed,
  Zap,
  Clock,
  Sparkles,
  Archive,
  ShieldAlert,
  Flag,
};

const COLOR_OPTIONS = [
  { value: 'text-quaternary', label: 'Grigio', hex: '#6b7280' },
  { value: 'accent-blue', label: 'Blu', hex: '#3b82f6' },
  { value: 'accent-emerald', label: 'Verde', hex: '#10b981' },
  { value: 'accent-amber', label: 'Ambra', hex: '#f59e0b' },
  { value: 'accent-rose', label: 'Rosa', hex: '#f43f5e' },
  { value: 'accent-violet', label: 'Viola', hex: '#8b5cf6' },
  { value: 'accent-cyan', label: 'Ciano', hex: '#06b6d4' },
];

const STATUS_PRESETS: Record<string, { label: string; options: StatusOption[] }> = {
  default: {
    label: 'Standard',
    options: [
      { value: 'open', label: 'Aperto', color: 'text-quaternary', icon: 'CircleDashed', category: 'open' },
      { value: 'in_progress', label: 'In corso', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'in_review', label: 'In revisione', color: 'accent-amber', icon: 'Clock', category: 'active' },
      { value: 'completed', label: 'Completato', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
      { value: 'closed', label: 'Chiuso', color: 'accent-violet', icon: 'Archive', category: 'closed' },
    ],
  },
  kanban: {
    label: 'Kanban',
    options: [
      { value: 'backlog', label: 'Backlog', color: 'text-quaternary', icon: 'CircleDashed', category: 'open' },
      { value: 'todo', label: 'To Do', color: 'accent-cyan', icon: 'Flag', category: 'open' },
      { value: 'in_progress', label: 'In corso', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'in_review', label: 'In revisione', color: 'accent-amber', icon: 'Clock', category: 'active' },
      { value: 'done', label: 'Fatto', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
    ],
  },
  bug_tracking: {
    label: 'Bug Tracking',
    options: [
      { value: 'new', label: 'Nuovo', color: 'accent-rose', icon: 'ShieldAlert', category: 'open' },
      { value: 'triaged', label: 'Triaged', color: 'accent-amber', icon: 'Flag', category: 'open' },
      { value: 'in_progress', label: 'In corso', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'fixed', label: 'Fixato', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
      { value: 'closed', label: 'Chiuso', color: 'accent-violet', icon: 'Archive', category: 'closed' },
    ],
  },
  scrum: {
    label: 'Scrum',
    options: [
      { value: 'backlog', label: 'Backlog', color: 'text-quaternary', icon: 'CircleDashed', category: 'open' },
      { value: 'sprint_ready', label: 'Sprint Ready', color: 'accent-cyan', icon: 'Flag', category: 'open' },
      { value: 'in_progress', label: 'In Progress', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'testing', label: 'Testing', color: 'accent-amber', icon: 'Clock', category: 'active' },
      { value: 'done', label: 'Done', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
    ],
  },
  simple: {
    label: 'Semplice',
    options: [
      { value: 'todo', label: 'Da fare', color: 'text-quaternary', icon: 'CircleDashed', category: 'open' },
      { value: 'in_progress', label: 'In corso', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'done', label: 'Fatto', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
    ],
  },
};

const DEFAULT_PRESET = STATUS_PRESETS.default.options;

type StatusCategory = 'open' | 'active' | 'closed';
type StatusOption = { value: string; label: string; color: string; icon?: string; category?: StatusCategory };

const CATEGORY_OPTIONS: { value: StatusCategory; label: string; abbr: string; color: string }[] = [
  { value: 'open', label: 'Aperto', abbr: 'A', color: '#6b7280' },
  { value: 'active', label: 'In corso', abbr: 'C', color: '#3b82f6' },
  { value: 'closed', label: 'Chiuso', abbr: 'X', color: '#10b981' },
];

// ─── Status chip inline editor ────────────────────────────────────────────────

function StatusOptionRow({
  option,
  onUpdate,
  onDelete,
  onDragStart,
  onDragOver,
  onDragEnd,
  index,
  readOnly = false,
}: {
  option: StatusOption;
  index: number;
  onUpdate: (idx: number, updates: Partial<StatusOption>) => void;
  onDelete: (idx: number) => void;
  onDragStart: (idx: number) => void;
  onDragOver: (idx: number) => void;
  onDragEnd: () => void;
  readOnly?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(option.label);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const colorBtnRef = useRef<HTMLButtonElement>(null);

  const colorHex = COLOR_OPTIONS.find((c) => c.value === option.color)?.hex ?? '#6b7280';
  const Icon = option.icon ? STATUS_ICONS[option.icon] : CircleDashed;

  const commitLabel = () => {
    const trimmed = label.trim();
    if (trimmed && trimmed !== option.label) {
      onUpdate(index, { label: trimmed, value: trimmed.toLowerCase().replace(/\s+/g, '_') });
    } else {
      setLabel(option.label);
    }
    setEditing(false);
  };

  const handleColorClick = () => {
    if (readOnly) return;
    if (!showColorPicker && colorBtnRef.current) {
      const r = colorBtnRef.current.getBoundingClientRect();
      setPickerPos(clampToViewport(r.left, r.bottom + 4, 140, 200));
    }
    setShowColorPicker((v) => !v);
  };

  return (
    <div
      draggable={!readOnly}
      onDragStart={() => !readOnly && onDragStart(index)}
      onDragOver={(e) => { e.preventDefault(); if (!readOnly) onDragOver(index); }}
      onDragEnd={onDragEnd}
      className={clsx(
        'group flex items-center gap-2 px-3 py-2 rounded-lg border border-border-primary bg-surface-tertiary/40 transition-colors',
        !readOnly && 'hover:border-border-secondary',
        readOnly && 'opacity-70'
      )}
    >
      {!readOnly && (
        <GripVertical className="w-3.5 h-3.5 text-text-quaternary opacity-0 group-hover:opacity-100 cursor-grab shrink-0" />
      )}

      {/* Color dot + icon */}
      <div className="shrink-0">
        <button
          ref={colorBtnRef}
          onClick={handleColorClick}
          disabled={readOnly}
          className={clsx(
            'flex items-center justify-center w-5 h-5 rounded-full transition-all',
            !readOnly && 'hover:ring-2 hover:ring-border-secondary'
          )}
          style={{ backgroundColor: colorHex + '33' }}
          title={readOnly ? undefined : 'Cambia colore'}
        >
          <span style={{ color: colorHex }}>{Icon && <Icon className="w-3 h-3" />}</span>
        </button>
        {showColorPicker && !readOnly && (
          <div
            className="fixed z-[999] p-2 rounded-lg border border-border-primary bg-surface-primary shadow-xl"
            style={{ top: pickerPos.top, left: pickerPos.left }}
          >
            <div className="grid grid-cols-4 gap-1.5 mb-2">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c.value}
                  onClick={() => { onUpdate(index, { color: c.value }); setShowColorPicker(false); }}
                  className="w-5 h-5 rounded-full hover:scale-110 transition-transform"
                  style={{ backgroundColor: c.hex }}
                  title={c.label}
                />
              ))}
            </div>
            <div className="border-t border-border-primary pt-2">
              <p className="text-[9px] text-text-quaternary mb-1.5 uppercase tracking-wider">Icona</p>
              <div className="grid grid-cols-4 gap-1">
                {Object.entries(STATUS_ICONS).map(([name, Ico]) => (
                  <button
                    key={name}
                    onClick={() => { onUpdate(index, { icon: name }); setShowColorPicker(false); }}
                    className={clsx(
                      'p-1 rounded hover:bg-surface-hover transition-colors',
                      option.icon === name && 'bg-accent-violet/10'
                    )}
                    title={name}
                  >
                    <Ico className="w-3.5 h-3.5 text-text-secondary" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Label */}
      {editing && !readOnly ? (
        <input
          ref={inputRef}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitLabel();
            if (e.key === 'Escape') { setLabel(option.label); setEditing(false); }
          }}
          autoFocus
          className="flex-1 text-[12px] bg-transparent border-b border-accent-violet text-text-primary outline-none pb-0.5"
        />
      ) : (
        <span
          className="flex-1 text-[12px] text-text-primary cursor-text"
          onDoubleClick={() => !readOnly && setEditing(true)}
          title={readOnly ? undefined : 'Doppio click per rinominare'}
        >
          {option.label}
        </span>
      )}

      {/* Category badge */}
      {!readOnly && (
        <button
          onClick={() => {
            const cats: StatusCategory[] = ['open', 'active', 'closed'];
            const currentIdx = cats.indexOf(option.category || 'open');
            const nextCat = cats[(currentIdx + 1) % cats.length];
            onUpdate(index, { category: nextCat });
          }}
          className="shrink-0 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors"
          style={{
            backgroundColor: (CATEGORY_OPTIONS.find(c => c.value === (option.category || 'open'))?.color || '#6b7280') + '20',
            color: CATEGORY_OPTIONS.find(c => c.value === (option.category || 'open'))?.color || '#6b7280',
          }}
          title={`Categoria: ${CATEGORY_OPTIONS.find(c => c.value === (option.category || 'open'))?.label}. Click per cambiare.`}
        >
          {CATEGORY_OPTIONS.find(c => c.value === (option.category || 'open'))?.label}
        </button>
      )}

      {!readOnly && (
        <button
          onClick={() => onDelete(index)}
          className="shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-accent-rose/10 transition-all"
          title="Elimina stato"
        >
          <Trash2 className="w-3.5 h-3.5 text-text-quaternary hover:text-accent-rose" />
        </button>
      )}
    </div>
  );
}

// ─── Inherited fields section (tag / campi) ──────────────────────────────────

function InheritedSection({
  spaceId,
  spaceName,
  filterType,
  excludeTypes,
}: {
  spaceId?: string;
  spaceName?: string;
  filterType?: string;
  excludeTypes?: string[];
}) {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [collapsed, setCollapsed] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!spaceId) return;
    setLoading(true);
    fetchCustomFields('project', spaceId)
      .then((all) => {
        let filtered = all;
        if (filterType) filtered = all.filter((f) => f.type === filterType);
        else if (excludeTypes?.length) filtered = all.filter((f) => !excludeTypes.includes(f.type));
        setFields(filtered);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [spaceId, filterType, excludeTypes]);

  if (!spaceId || loading || fields.length === 0) return null;

  return (
    <div className="mt-6 pt-4 border-t border-border-primary/50">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 text-[11px] text-text-tertiary hover:text-text-secondary transition-colors w-full"
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        <Layers className="w-3.5 h-3.5" />
        Ereditati da {spaceName || 'spazio'} ({fields.length})
      </button>
      {!collapsed && (
        <div className="mt-2 space-y-1.5 opacity-60">
          {fields.map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border-primary/40 bg-surface-tertiary/30"
            >
              <span className="text-[12px] text-text-secondary font-medium">{f.name}</span>
              {f.options && f.options.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {f.options.slice(0, 5).map((opt) => (
                    <span key={opt.value} className="px-1.5 py-0.5 rounded text-[9px] bg-surface-active text-text-tertiary">
                      {opt.label}
                    </span>
                  ))}
                  {f.options.length > 5 && (
                    <span className="text-[9px] text-text-quaternary">+{f.options.length - 5}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Statuses manager (list-level with space inheritance) ─────────────────────

function ListStatusesManager({ listId, spaceId, spaceName, orgId }: { listId?: string; spaceId?: string; spaceName?: string; orgId?: string }) {
  const { user } = useAuth();
  const [options, setOptions] = useState<StatusOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [inherited, setInherited] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('text-quaternary');
  const [newIcon, setNewIcon] = useState('CircleDashed');
  const [showNewColorPicker, setShowNewColorPicker] = useState(false);
  const [newPickerPos, setNewPickerPos] = useState({ top: 0, left: 0 });
  const newColorBtnRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    if (!listId) { setLoading(false); return; }
    setLoading(true);
    let loadedFromDefaults = false;
    try {
      // Try list-level first
      const listRows = await fetchTaskStatuses('list', listId);
      if (listRows.length > 0) {
        setOptions(listRows.map((r) => ({ value: r.value, label: r.name, color: r.color ?? 'text-quaternary', icon: r.icon ?? undefined, category: (r as unknown as { category?: StatusCategory }).category || 'open' })));
        setInherited(false);
      } else if (spaceId) {
        // Fall back to space-level
        const spaceRows = await fetchTaskStatuses('project', spaceId);
        if (spaceRows.length > 0) {
          setOptions(spaceRows.map((r) => ({ value: r.value, label: r.name, color: r.color ?? 'text-quaternary', icon: r.icon ?? undefined, category: (r as unknown as { category?: StatusCategory }).category || 'open' })));
          setInherited(true);
        } else {
          // No saved statuses anywhere — show default preset as starting point
          setOptions(DEFAULT_PRESET);
          setInherited(false);
          loadedFromDefaults = true;
        }
      } else {
        // No space context — show default preset as starting point
        setOptions(DEFAULT_PRESET);
        setInherited(false);
        loadedFromDefaults = true;
      }
    } catch (e) {
      handleError(e, { level: 'warning', message: 'Errore caricamento stati' });
    } finally {
      setLoading(false);
      setDirty(loadedFromDefaults);
    }
  }, [listId, spaceId]);

  useEffect(() => { load(); }, [load]);

  const save = async (newOptions: StatusOption[]) => {
    if (!listId) return;
    setSaving(true);
    try {
      await saveTaskStatuses(
        newOptions.map((o) => ({ value: o.value, name: o.label, color: o.color, icon: o.icon, category: o.category || 'open' })),
        'list',
        listId
      );
      setOptions(newOptions);
      setInherited(false);
      setDirty(false);
      toast('Stati salvati', 'success');
    } catch (e) {
      handleError(e, { level: 'error', message: 'Errore nel salvataggio stati' });
    } finally {
      setSaving(false);
    }
  };

  const personalizza = async () => {
    // Create a list-level copy of the inherited statuses
    await save(options);
  };

  const ripristina = async () => {
    if (inherited || !listId) return;
    try {
      await deleteTaskStatusesByScope('list', listId);
      await load();
      toast('Ripristinati gli stati dello spazio', 'success');
    } catch (e) {
      handleError(e, { level: 'error', message: 'Errore nel ripristino' });
    }
  };

  const [showPresets, setShowPresets] = useState(false);
  const [presetMenuPos, setPresetMenuPos] = useState({ top: 0, left: 0 });
  const presetBtnRef = useRef<HTMLButtonElement>(null);
  const [savedPresets, setSavedPresets] = useState<StatusPreset[]>([]);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');

  const loadSavedPresets = useCallback(async () => {
    if (!orgId) return;
    try {
      const presets = await fetchStatusPresets(orgId);
      setSavedPresets(presets);
    } catch { /* ignore */ }
  }, [orgId]);

  useEffect(() => { loadSavedPresets(); }, [loadSavedPresets]);

  const loadPreset = (presetKey?: string) => {
    const preset = presetKey ? STATUS_PRESETS[presetKey]?.options : DEFAULT_PRESET;
    if (preset) {
      setOptions(preset);
      setDirty(true);
      setShowPresets(false);
    }
  };

  const loadSavedPreset = (preset: StatusPreset) => {
    setOptions(preset.statuses.map(s => ({ value: s.value, label: s.name, color: s.color ?? 'text-quaternary', icon: s.icon ?? undefined })));
    setDirty(true);
    setShowPresets(false);
  };

  const handleSavePreset = async () => {
    if (!presetName.trim() || !orgId) return;
    try {
      await createStatusPreset(
        orgId,
        presetName.trim(),
        options.map(o => ({ value: o.value, name: o.label, color: o.color, icon: o.icon })),
        user?.id,
      );
      toast('Preset salvato', 'success');
      setShowSavePreset(false);
      setPresetName('');
      await loadSavedPresets();
    } catch {
      toast('Errore nel salvataggio del preset', 'error');
    }
  };

  const handleDeletePreset = async (presetId: string) => {
    try {
      await deleteStatusPreset(presetId);
      setSavedPresets(prev => prev.filter(p => p.id !== presetId));
      toast('Preset eliminato', 'success');
    } catch {
      toast('Errore nell\'eliminazione', 'error');
    }
  };

  const handleUpdate = (idx: number, updates: Partial<StatusOption>) => {
    const next = options.map((o, i) => i === idx ? { ...o, ...updates } : o);
    setOptions(next);
    setDirty(true);
  };

  const handleDelete = (idx: number) => {
    const next = options.filter((_, i) => i !== idx);
    setOptions(next);
    setDirty(true);
  };

  const handleDragOver = (idx: number) => {
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...options];
    const [removed] = next.splice(dragIdx, 1);
    next.splice(idx, 0, removed);
    setOptions(next);
    setDragIdx(idx);
    setDirty(true);
  };

  const handleAddStatus = () => {
    if (!newLabel.trim()) return;
    const next = [
      ...options,
      {
        value: newLabel.trim().toLowerCase().replace(/\s+/g, '_'),
        label: newLabel.trim(),
        color: newColor,
        icon: newIcon,
      },
    ];
    setOptions(next);
    setDirty(true);
    setNewLabel('');
    setNewColor('text-quaternary');
    setNewIcon('CircleDashed');
    setShowAddForm(false);
  };

  if (!listId) {
    return (
      <div className="text-center py-8 text-[11px] text-text-tertiary">
        Seleziona una lista per gestire i suoi stati.
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-8 text-[11px] text-text-quaternary">Caricamento…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[13px] font-semibold text-text-primary">Stati della lista</h3>
          <p className="text-[11px] text-text-tertiary mt-0.5">
            {inherited
              ? `Stai usando gli stati dello spazio ${spaceName ? `"${spaceName}"` : ''}.`
              : 'Definisci il workflow dei task. Trascina per riordinare, doppio click per rinominare.'}
          </p>
        </div>
        {!inherited && spaceId && (
          <button
            onClick={ripristina}
            className="text-[11px] text-text-quaternary hover:text-text-tertiary underline underline-offset-2 transition-colors"
            title="Torna a usare gli stati dello spazio"
          >
            Ripristina da spazio
          </button>
        )}
      </div>

      {/* Inherited banner */}
      {inherited && (
        <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-accent-violet/5 border border-accent-violet/20">
          <p className="text-[11px] text-text-secondary">
            Gli stati sono ereditati dallo spazio. Personalizzali per questa lista specifica.
          </p>
          <button
            onClick={personalizza}
            disabled={saving}
            className="shrink-0 ml-3 px-3 py-1.5 rounded-lg bg-accent-violet text-white text-[11px] font-medium hover:bg-accent-violet/90 transition-colors disabled:opacity-60"
          >
            {saving ? 'Salvo…' : 'Personalizza'}
          </button>
        </div>
      )}

      {/* Status list */}
      <div className="space-y-1.5">

        {options.map((opt, idx) => (
          <StatusOptionRow
            key={opt.value + idx}
            option={opt}
            index={idx}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onDragStart={setDragIdx}
            onDragOver={handleDragOver}
            onDragEnd={() => setDragIdx(null)}
            readOnly={inherited}
          />
        ))}
      </div>

      {/* Add / actions footer (only when not inherited) */}
      {!inherited && (
        showAddForm ? (
          <div className="p-3 rounded-lg border border-accent-violet/30 bg-accent-violet/5 space-y-3">
            <div className="flex items-center gap-2">
              <div>
                <button
                  ref={newColorBtnRef}
                  onClick={() => {
                    if (!showNewColorPicker && newColorBtnRef.current) {
                      const r = newColorBtnRef.current.getBoundingClientRect();
                      setNewPickerPos({ top: r.bottom + 4, left: r.left });
                    }
                    setShowNewColorPicker((v) => !v);
                  }}
                  className="flex items-center justify-center w-7 h-7 rounded-full border border-border-primary hover:border-accent-violet transition-colors"
                  style={{ backgroundColor: (COLOR_OPTIONS.find((c) => c.value === newColor)?.hex ?? '#6b7280') + '33' }}
                >
                  {(() => { const Ico = STATUS_ICONS[newIcon]; const col = COLOR_OPTIONS.find((c) => c.value === newColor)?.hex; return <span style={{ color: col }}><Ico className="w-3.5 h-3.5" /></span>; })()}
                </button>
                {showNewColorPicker && (
                  <div
                    className="fixed z-[999] p-2 rounded-lg border border-border-primary bg-surface-primary shadow-xl"
                    style={{ top: newPickerPos.top, left: newPickerPos.left }}
                  >
                    <div className="grid grid-cols-4 gap-1.5 mb-2">
                      {COLOR_OPTIONS.map((c) => (
                        <button key={c.value} onClick={() => { setNewColor(c.value); }}
                          className={clsx('w-5 h-5 rounded-full hover:scale-110 transition-transform', newColor === c.value && 'ring-2 ring-white ring-offset-1')}
                          style={{ backgroundColor: c.hex }} title={c.label} />
                      ))}
                    </div>
                    <div className="border-t border-border-primary pt-2">
                      <p className="text-[9px] text-text-quaternary mb-1 uppercase tracking-wider">Icona</p>
                      <div className="grid grid-cols-4 gap-1">
                        {Object.entries(STATUS_ICONS).map(([name, Ico]) => (
                          <button key={name} onClick={() => { setNewIcon(name); setShowNewColorPicker(false); }}
                            className={clsx('p-1 rounded hover:bg-surface-hover', newIcon === name && 'bg-accent-violet/10')}>
                            <Ico className="w-3.5 h-3.5 text-text-secondary" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddStatus(); if (e.key === 'Escape') setShowAddForm(false); }}
                placeholder="Nome stato…"
                autoFocus
                className="flex-1 px-2 py-1.5 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-text-primary focus:border-accent-violet focus:ring-1 focus:ring-accent-violet"
              />
              <button onClick={handleAddStatus} disabled={!newLabel.trim()}
                className="px-3 py-1.5 rounded-lg bg-accent-violet text-white text-[11px] font-medium disabled:opacity-40 hover:bg-accent-violet/90">
                Aggiungi
              </button>
              <button onClick={() => setShowAddForm(false)} className="p-1.5 rounded hover:bg-surface-hover text-text-quaternary">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border-secondary text-[11px] text-text-tertiary hover:text-text-secondary hover:border-text-tertiary transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              Aggiungi stato
            </button>
            <div className="relative">
              <button
                ref={presetBtnRef}
                onClick={() => {
                  if (!showPresets && presetBtnRef.current) {
                    const r = presetBtnRef.current.getBoundingClientRect();
                    setPresetMenuPos({ top: r.bottom + 4, left: r.left });
                  }
                  setShowPresets(v => !v);
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border-secondary text-[11px] text-text-tertiary hover:text-accent-violet hover:border-accent-violet/50 transition-all"
              >
                <ChevronDown className="w-3.5 h-3.5" />
                Usa preset
              </button>
              {showPresets && (
                <div className="fixed z-[999] bg-surface-primary border border-border-primary rounded-lg shadow-2xl min-w-[220px] py-1 animate-fade-in" style={{ top: presetMenuPos.top, left: presetMenuPos.left }}>
                  {savedPresets.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-text-quaternary font-semibold">Salvati</div>
                      {savedPresets.map(p => (
                        <div key={p.id} className="flex items-center group">
                          <button
                            onClick={() => loadSavedPreset(p)}
                            className="flex-1 flex items-center gap-2 px-3 py-2 text-left text-[11px] text-text-primary hover:bg-surface-hover transition-colors"
                          >
                            <span className="font-medium truncate">{p.name}</span>
                            <span className="text-text-quaternary ml-auto shrink-0">{p.statuses.length} stati</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeletePreset(p.id); }}
                            className="p-1.5 mr-1 rounded opacity-0 group-hover:opacity-100 hover:bg-accent-rose/10 transition-all"
                          >
                            <Trash2 className="w-3 h-3 text-text-quaternary hover:text-accent-rose" />
                          </button>
                        </div>
                      ))}
                      <div className="border-t border-border-primary my-1" />
                    </>
                  )}
                  <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-text-quaternary font-semibold">Predefiniti</div>
                  {Object.entries(STATUS_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => loadPreset(key)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-[11px] text-text-primary hover:bg-surface-hover transition-colors"
                    >
                      <span className="font-medium">{preset.label}</span>
                      <span className="text-text-quaternary ml-auto">{preset.options.length} stati</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {options.length > 0 && !showSavePreset && (
              <button
                onClick={() => setShowSavePreset(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border-secondary text-[11px] text-text-tertiary hover:text-accent-emerald hover:border-accent-emerald/50 transition-all"
              >
                <Plus className="w-3.5 h-3.5" />
                Salva come preset
              </button>
            )}
            {showSavePreset && (
              <div className="flex items-center gap-2">
                <input
                  value={presetName}
                  onChange={e => setPresetName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); if (e.key === 'Escape') setShowSavePreset(false); }}
                  placeholder="Nome preset..."
                  autoFocus
                  className="px-2 py-1.5 rounded-lg bg-surface-tertiary border border-border-primary text-[11px] text-text-primary focus:border-accent-emerald focus:ring-1 focus:ring-accent-emerald w-36"
                />
                <button onClick={handleSavePreset} disabled={!presetName.trim()}
                  className="px-2.5 py-1.5 rounded-lg bg-accent-emerald text-white text-[11px] font-medium disabled:opacity-40 hover:bg-accent-emerald/90">
                  Salva
                </button>
                <button onClick={() => setShowSavePreset(false)} className="p-1 rounded hover:bg-surface-hover text-text-quaternary">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {dirty && (
              <button
                onClick={() => save(options)}
                disabled={saving}
                className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent-violet text-white text-[11px] font-medium hover:bg-accent-violet/90 disabled:opacity-60"
              >
                <Check className="w-3.5 h-3.5" />
                {saving ? 'Salvo…' : 'Salva stati'}
              </button>
            )}
          </div>
        )
      )}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface ListSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  listName: string;
  folderName?: string | null;
  spaceName: string;
  spaceId?: string;
  listId?: string;
  folderId?: string;
}

export function ListSettingsModal({
  isOpen,
  onClose,
  listName,
  folderName,
  spaceName,
  spaceId,
  listId,
  folderId,
}: ListSettingsModalProps) {
  const { activeTab, setActiveTab } = useModal<'general' | 'stati' | 'tag' | 'campi'>(isOpen, onClose, 'general');
  const { currentOrg } = useOrganization();
  const modalRef = useRef<HTMLDivElement>(null);
  const displayName = folderName || listName;
  const [name, setName] = useState(displayName);

  useEffect(() => {
    setName(displayName);
  }, [displayName]);

  const ids = useMemo(
    () => ({
      title: generateA11yId('list-settings-title'),
      description: generateA11yId('list-settings-desc'),
    }),
    []
  );

  useEffect(() => {
    if (isOpen && modalRef.current) {
      return trapFocus(modalRef.current);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const scopeId = folderName ? folderId : listId;
  const scope = folderName ? 'folder' : 'list';

  const headerTitle =
    activeTab === 'general' ? `Impostazioni ${folderName ? 'Cartella' : 'Lista'}` :
    activeTab === 'stati' ? 'Stati personalizzati' :
    activeTab === 'tag' ? 'Tag' :
    'Campi personalizzati';

  const headerSub =
    activeTab === 'general' ? `${displayName} in ${spaceName}` :
    activeTab === 'stati' ? `Workflow della ${folderName ? 'cartella' : 'lista'} ${displayName}` :
    activeTab === 'tag' ? `Tag disponibili per i task in ${displayName}` :
    `Campi personalizzati per ${displayName}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose} aria-hidden="true" />

      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ids.title}
        aria-describedby={ids.description}
        className="relative w-full max-w-3xl max-h-[85vh] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl animate-scale-in flex overflow-hidden"
      >
        {/* Sidebar */}
        <div className="w-52 bg-surface-tertiary/50 border-r border-border-primary p-3 flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 mb-4">
            {folderName ? <Folder className="w-5 h-5 text-text-tertiary" /> : <Hash className="w-5 h-5 text-text-tertiary" />}
            <span className="text-[14px] font-semibold text-text-primary">Impostazioni</span>
          </div>

          <nav className="space-y-1" role="tablist">
            {([
              { id: 'general', label: 'Generale', icon: Settings },
              { id: 'stati', label: 'Stati', icon: CircleDashed },
              { id: 'tag', label: 'Tag', icon: Tag },
              { id: 'campi', label: 'Campi', icon: Layers },
            ] as const).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                role="tab"
                aria-selected={activeTab === id}
                onClick={() => setActiveTab(id)}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all',
                  activeTab === id
                    ? 'bg-accent-violet/10 text-accent-violet'
                    : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
            <div>
              <h2 id={ids.title} className="text-[15px] font-semibold text-text-primary">{headerTitle}</h2>
              <p id={ids.description} className="text-[11px] text-text-tertiary">{headerSub}</p>
            </div>
            <button
              onClick={onClose}
              aria-label="Chiudi impostazioni"
              className="p-1.5 rounded-md hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-tertiary"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'general' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-[13px] font-semibold text-text-primary mb-1">
                    Informazioni {folderName ? 'cartella' : 'lista'}
                  </h3>
                  <p className="text-[11px] text-text-tertiary mb-4">
                    Configura le informazioni base della {folderName ? 'cartella' : 'lista'}
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[11px] font-medium text-text-secondary mb-1.5">
                        Nome {folderName ? 'cartella' : 'lista'}
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={async () => {
                          const trimmed = name.trim();
                          if (trimmed && trimmed !== displayName) {
                            const targetId = folderName ? folderId : listId;
                            if (targetId) {
                              try {
                                await renameSpace(targetId, trimmed);
                              } catch (error) {
                                handleError(error, { level: 'error', message: 'Error renaming' });
                                setName(displayName);
                              }
                            }
                          }
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-text-primary placeholder:text-text-quaternary focus:border-accent-violet focus:ring-1 focus:ring-accent-violet transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-text-secondary mb-1.5">Spazio</label>
                      <input type="text" value={spaceName} disabled
                        className="w-full px-3 py-2 rounded-lg bg-surface-tertiary/50 border border-border-primary text-[12px] text-text-quaternary cursor-not-allowed" />
                    </div>
                    {folderName && (
                      <div>
                        <label className="block text-[11px] font-medium text-text-secondary mb-1.5">Cartella</label>
                        <input type="text" value={folderName} disabled
                          className="w-full px-3 py-2 rounded-lg bg-surface-tertiary/50 border border-border-primary text-[12px] text-text-quaternary cursor-not-allowed" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'stati' && (
              <ListStatusesManager listId={listId} spaceId={spaceId} spaceName={spaceName} orgId={currentOrg?.id} />
            )}

            {activeTab === 'tag' && (
              <TagsManager
                scope={scope}
                scopeId={scopeId}
                spaceId={spaceId}
                spaceName={spaceName}
                orgId={currentOrg?.id}
                orgName={currentOrg?.name}
              />
            )}

            {activeTab === 'campi' && (
              <div>
                <CustomFieldsManager
                  scope={scope}
                  scopeId={scopeId}
                 
                />
                <InheritedSection
                  spaceId={spaceId}
                  spaceName={spaceName}
                 
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
